import {
  Avatar,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import BirthDayIcon from "../icons/BirthDayIcon";
import JubileeIcon from "../icons/JubileeIcon";
import React, { useContext, useEffect, useState } from "react";
import { ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import useAfasData from "../hooks/useAfasData";
import dayjs from "dayjs";
import { LanguageContext } from "../context/LanguageContext";

type CelebrationType = "birthday" | "jubilee";

interface Celebration {
  id: string;
  employeeName: string;
  type: CelebrationType;
  date: string;
  iso_date: string;
  description: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAvatarColorByType = (type: CelebrationType, theme: any) => {
  switch (type) {
    case "birthday":
      return theme.palette.purple.main;
    case "jubilee":
      return theme.palette.primary.main;
    default:
      return theme.palette.grey[500];
  }
};

const getIconByType = (type: CelebrationType) => {
  switch (type) {
    case "birthday":
      return <BirthDayIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    case "jubilee":
      return <JubileeIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    default:
      return <BirthDayIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
  }
};

const BirthdayJubileeComponent: React.FC = () => {
  const { strings } = useContext(LanguageContext);
  const rowsPerPage = 7;
  const theme: Theme = useTheme();
  const [page, setPage] = useState(0);
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);
  const { data: celebrationData, isLoading: celebrationDataLoading } = useAfasData("/celebrations/overview");
  useEffect(() => {
    if (!celebrationDataLoading && celebrationData) {
      celebrationData.sort((a: Celebration, b: Celebration) => {
        return dayjs(a.iso_date).isAfter(dayjs(b.iso_date)) ? 1 : -1;
      });
      setCelebrations(celebrationData);
    }
  }, [celebrationData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Calculate pagination info
  const totalPages =
    Math.ceil(celebrations.length / rowsPerPage) > 0 ? Math.ceil(celebrations.length / rowsPerPage) : 1;
  const currentPageStart = page * rowsPerPage;
  const currentPageEnd = Math.min((page + 1) * rowsPerPage, celebrations.length);
  const currentCelebrations = celebrations.slice(currentPageStart, currentPageEnd);

  return (
    <Paper
      elevation={0}
      sx={(theme: Theme) => ({
        height: "100%",
        bgcolor: theme.palette.light.main,
        py: 4,
        px: 3,
      })}
    >
      <Stack>
        <Typography mb={4} variant="h3">
          {strings.celebration.title}
        </Typography>
        <Typography mb={2} variant="subtitle1" fontWeight={"bold"}>
          {strings.celebration.upcoming}
        </Typography>
        <List disablePadding sx={{ mb: 2 }}>
          {currentCelebrations.map((celebration) => (
            <ListItem key={celebration.id} sx={{ py: ".5rem", px: 0, display: "block" }}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{
                    width: "30px",
                    height: "30px",
                    bgcolor: getAvatarColorByType(celebration.type, theme),
                    mr: 2,
                  }}
                >
                  {getIconByType(celebration.type)}
                </Avatar>
                <Box display="flex" alignItems="center">
                  {celebration.employeeName} - {celebration.description}
                  {celebration.id.includes("abp") && <Chip label="ABP" size="small" sx={{ ml: 1 }} />}
                  {celebration.id.includes("service") && <Chip label="UT" size="small" sx={{ ml: 1 }} />}
                </Box>
                <Typography variant="body1" sx={{ ml: "auto" }}>
                  {celebration.date}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 3,
          }}
        >
          {totalPages !== 1 && (
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => handleChangePage(null, page - 1)} disabled={page === 0} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography component="span" sx={{ mx: 1 }}>
                {page + 1}/
                <Typography component={"span"} fontWeight={"bold"}>
                  {totalPages}
                </Typography>
              </Typography>
              <IconButton
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= totalPages - 1}
                size="small"
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default BirthdayJubileeComponent;
